import mammoth from 'mammoth';
import JSZip from 'jszip';

export type PlateContent = any[];

export interface ImportOptions {
  preserveFormatting?: boolean;
  convertHeadings?: boolean;
  maxHeadingLevel?: number;
}

/**
 * Import content from DOCX file
 * @param file - The DOCX file to import
 * @param options - Import options
 * @returns Plate editor content
 */
export const importFromDocx = async (file: File, options: ImportOptions = {}): Promise<PlateContent> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });
    
    if (result.messages.length > 0) {
      console.warn('DOCX import warnings:', result.messages);
    }
    
    return htmlToPlateNodes(result.value, options);
  } catch (error) {
    console.error('Error importing DOCX file:', error);
    throw new Error('Failed to import DOCX file');
  }
};

/**
 * Import content from ODT file
 * @param file - The ODT file to import
 * @param options - Import options
 * @returns Plate editor content
 */
export const importFromOdt = async (file: File, options: ImportOptions = {}): Promise<PlateContent> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);
    const contentXml = await zip.file('content.xml')?.async('string');
    
    if (!contentXml) {
      throw new Error('Invalid ODT file: content.xml not found');
    }
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(contentXml, 'text/xml');
    
    // Handle namespaced elements
    const textElements = doc.querySelectorAll('text\\:h, text\\:p');
    
    const nodes: PlateContent = [];
    textElements.forEach(element => {
      const textContent = element.textContent?.trim();
      if (!textContent) return;
      
      const tagName = element.tagName;
      if (tagName.includes(':h')) {
        const level = element.getAttribute('text:outline-level') || '1';
        let headingType = 'h1';
        
        if (options.convertHeadings !== false) {
          const maxLevel = options.maxHeadingLevel || 3;
          const levelNum = Math.min(parseInt(level), maxLevel);
          headingType = levelNum === 1 ? 'h1' : levelNum === 2 ? 'h2' : 'h3';
        }
        
        nodes.push({ type: headingType, children: [{ text: textContent }] });
      } else {
        nodes.push({ type: 'p', children: [{ text: textContent }] });
      }
    });
    
    return nodes.length > 0 ? nodes : [{ type: 'p', children: [{ text: '' }] }];
  } catch (error) {
    console.error('Error importing ODT file:', error);
    throw new Error('Failed to import ODT file');
  }
};

/**
 * Import content from HTML file
 * @param file - The HTML file to import
 * @param options - Import options
 * @returns Plate editor content
 */
export const importFromHtml = async (file: File, options: ImportOptions = {}): Promise<PlateContent> => {
  try {
    const htmlContent = await file.text();
    return htmlToPlateNodes(htmlContent, options);
  } catch (error) {
    console.error('Error importing HTML file:', error);
    throw new Error('Failed to import HTML file');
  }
};

/**
 * Import content from Markdown file
 * @param file - The Markdown file to import
 * @param options - Import options
 * @returns Plate editor content
 */
export const importFromMarkdown = async (file: File, options: ImportOptions = {}): Promise<PlateContent> => {
  try {
    const markdownContent = await file.text();
    return markdownToPlateNodes(markdownContent, options);
  } catch (error) {
    console.error('Error importing Markdown file:', error);
    throw new Error('Failed to import Markdown file');
  }
};

/**
 * Import content from plain text file
 * @param file - The text file to import
 * @param options - Import options
 * @returns Plate editor content
 */
export const importFromText = async (file: File, options: ImportOptions = {}): Promise<PlateContent> => {
  try {
    const textContent = await file.text();
    return textToPlateNodes(textContent, options);
  } catch (error) {
    console.error('Error importing text file:', error);
    throw new Error('Failed to import text file');
  }
};

/**
 * Convert HTML to Plate editor format
 */
const htmlToPlateNodes = (html: string, options: ImportOptions = {}): PlateContent => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  
  const convertElement = (element: Element): any => {
    const tagName = element.tagName.toLowerCase();
    
    // Extract text content with basic formatting
    const children: any[] = [];
    Array.from(element.childNodes).forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          children.push({ text });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const childElement = node as Element;
        const childTag = childElement.tagName.toLowerCase();
        const childText = childElement.textContent?.trim();
        
        if (childText && options.preserveFormatting !== false) {
          if (childTag === 'strong' || childTag === 'b') {
            children.push({ text: childText, bold: true });
          } else if (childTag === 'em' || childTag === 'i') {
            children.push({ text: childText, italic: true });
          } else {
            children.push({ text: childText });
          }
        } else if (childText) {
          children.push({ text: childText });
        }
      }
    });
    
    if (children.length === 0) {
      children.push({ text: element.textContent?.trim() || '' });
    }
    
    // Convert to appropriate Plate node type
    switch (tagName) {
      case 'h1':
        return { type: 'h1', children };
      case 'h2':
        return { type: 'h2', children };
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return { type: 'h3', children };
      case 'blockquote':
        return { type: 'blockquote', children };
      case 'p':
      case 'div':
        return { type: 'p', children };
      default:
        return { type: 'p', children };
    }
  };
  
  const nodes: PlateContent = [];
  Array.from(body.children).forEach(element => {
    if (element.textContent?.trim()) {
      nodes.push(convertElement(element));
    }
  });
  
  // Fallback: extract all text if no structured content found
  if (nodes.length === 0) {
    const allText = body.textContent?.trim();
    if (allText) {
      const lines = allText.split('\n').filter(line => line.trim());
      lines.forEach(line => {
        nodes.push({ type: 'p', children: [{ text: line.trim() }] });
      });
    }
  }
  
  return nodes.length > 0 ? nodes : [{ type: 'p', children: [{ text: '' }] }];
};

/**
 * Convert Markdown to Plate editor format
 */
const markdownToPlateNodes = (markdown: string, options: ImportOptions = {}): PlateContent => {
  const lines = markdown.split('\n');
  const nodes: PlateContent = [];
  let inBlockquote = false;
  let blockquoteLines: string[] = [];
  
  const processBlockquote = () => {
    if (blockquoteLines.length > 0) {
      const blockquoteText = blockquoteLines.join(' ').trim();
      nodes.push({ type: 'blockquote', children: [{ text: blockquoteText }] });
      blockquoteLines = [];
      inBlockquote = false;
    }
  };
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    
    if (!trimmedLine) {
      if (inBlockquote) processBlockquote();
      return;
    }
    
    // Handle headings
    if (trimmedLine.startsWith('# ')) {
      if (inBlockquote) processBlockquote();
      nodes.push({ type: 'h1', children: [{ text: trimmedLine.substring(2).trim() }] });
    } else if (trimmedLine.startsWith('## ')) {
      if (inBlockquote) processBlockquote();
      nodes.push({ type: 'h2', children: [{ text: trimmedLine.substring(3).trim() }] });
    } else if (trimmedLine.startsWith('### ')) {
      if (inBlockquote) processBlockquote();
      nodes.push({ type: 'h3', children: [{ text: trimmedLine.substring(4).trim() }] });
    } else if (trimmedLine.match(/^#{4,6}\s/)) {
      if (inBlockquote) processBlockquote();
      const text = trimmedLine.replace(/^#+\s*/, '');
      nodes.push({ type: 'h3', children: [{ text: text.trim() }] });
    } 
    // Handle blockquotes
    else if (trimmedLine.startsWith('> ')) {
      const blockquoteText = trimmedLine.substring(2).trim();
      if (inBlockquote) {
        blockquoteLines.push(blockquoteText);
      } else {
        inBlockquote = true;
        blockquoteLines = [blockquoteText];
      }
    } 
    // Handle regular paragraphs
    else {
      if (inBlockquote) processBlockquote();
      
      // Basic formatting cleanup
      let text = trimmedLine;
      if (options.preserveFormatting === false) {
        text = text.replace(/\*\*(.*?)\*\*/g, '$1')
                  .replace(/\*(.*?)\*/g, '$1')
                  .replace(/_(.*?)_/g, '$1');
      }
      
      nodes.push({ type: 'p', children: [{ text }] });
    }
  });
  
  if (inBlockquote) processBlockquote();
  
  return nodes.length > 0 ? nodes : [{ type: 'p', children: [{ text: '' }] }];
};

/**
 * Convert plain text to Plate editor format
 */
const textToPlateNodes = (text: string, options: ImportOptions = {}): PlateContent => {
  const lines = text.split('\n');
  const nodes: PlateContent = [];
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine) {
      nodes.push({ type: 'p', children: [{ text: trimmedLine }] });
    }
  });
  
  return nodes.length > 0 ? nodes : [{ type: 'p', children: [{ text: '' }] }];
};
